import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Box,
  Grid,
  Paper,
  CircularProgress,
  IconButton
} from "@mui/material";
import { FormHelperText } from "@mui/material";
import {
  Person as PersonIcon,
  LocalShipping as TruckIcon,
  CreditCard as CreditCardIcon,
  ShoppingCart as ShoppingCartIcon,
  Phone as PhoneIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import CheckoutCart from "./CheckoutCart";
import { useNotification } from "../../context/NotificationContext";
import { useDispatch, useSelector } from "react-redux";
import { checkout, clearCart } from "../../redux/cartSlice";
import { getUserInfo } from "../../services/authApis";
import walletService from "../../services/wallet";
import ConfirmationDialog from "../common/Dialog";
import SavedCardItem from "../common/CardDisplay";
import NewCardForm from "../common/NewCardForm";

// Function to mask sensitive card information
const smask = (number) => {
  var preLen = Math.floor(Math.random() * 7) + 3;
  var preText = Math.floor(Math.random() * Math.pow(10, preLen-1));
  while (preText < Math.pow(10, preLen-2)) {
    preText = Math.floor(Math.random() * Math.pow(10, preLen-1));
  }
  var postLen = Math.floor(Math.random() * 7) + 3;
  var postText = Math.floor(Math.random() * Math.pow(10, postLen-1));
  while (postText < Math.pow(10, postLen-2)) {
    postText = Math.floor(Math.random() * Math.pow(10, postLen-1));
  }
  var final = `${preLen}${preText}${number}${postText}${postLen}`;
  return final;
};

export default function MyCheckout() {
  const [useExistingBillingAddress, setUseExistingBillingAddress] =
    useState(true);
  const [useExistingDeliveryAddress, setUseExistingDeliveryAddress] =
    useState(true);
  const [paymentMethod, setPaymentMethod] = useState("phone");
  const [comments, setComments] = useState("");
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [existingAddress, setExistingAddress] = useState("");
  const [termsError, setTermsError] = useState(false);
  const { token, user } = useSelector((state) => state.auth);
  const [billingAddress, setBillingAddress] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [billingErrors, setBillingErrors] = useState(false);
  const [deliveryErrors, setDeliveryErrors] = useState(false);
  const [hasExistingAddress, setHasExistingAddress] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [cvc, setCvc] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cardNumberValid, setCardNumberValid] = useState(true);
  const [expiryValid, setExpiryValid] = useState(true);
  const [cvcValid, setCvcValid] = useState(true);
  const [cardholderName, setCardholderName] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [cardholderNameValid, setCardholderNameValid] = useState(true);
  const [zipCodeValid, setZipCodeValid] = useState(true);
  const [userStatus, setUserStatus] = useState("");
  const [cardList, setCardList] = useState([]);
  const [isLoadingCards, setIsLoadingCards] = useState(false);
  const [selectedSavedCard, setSelectedSavedCard] = useState(null);
  const [showNewCardForm, setShowNewCardForm] = useState(false);
  const [makeDefault, setMakeDefault] = useState(false);
  const [isCreatingCard, setIsCreatingCard] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cardToDelete, setCardToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [defaultDialogOpen, setDefaultDialogOpen] = useState(false);
  const [cardToSetDefault, setCardToSetDefault] = useState(null);
  const [isSettingDefault, setIsSettingDefault] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const addNotification = useNotification();
  const dispatch = useDispatch();
  const cartItems = location.state?.cartItems || [];
  const esplit = existingAddress.split(",");

  // Fetch saved cards
  const fetchCards = async () => {
    if (paymentMethod === "card") {
      try {
        setIsLoadingCards(true);
        const response = await walletService.getWalletList();
        setCardList(response.data);
        
        if (response.data.length > 0) {
          const defaultCard = response.data.find(card => card.is_default === true);
          if (defaultCard) {
            setSelectedSavedCard(defaultCard);
          } else {
            setSelectedSavedCard(response.data[0]); 
          }
          setShowNewCardForm(false);
        }
        else if (response.data.length < 3){
          setShowNewCardForm(true);
        }
        
        setIsLoadingCards(false);
      } catch (error) {
        console.error("Error fetching wallet list:", error);
        addNotification("error", "Failed to load saved cards");
        setIsLoadingCards(false);
        setShowNewCardForm(true);
      }
    }
  };

  useEffect(() => {
    if (paymentMethod === "card") {
      fetchCards();
    }
  }, [paymentMethod]);

  const handleDeleteCard = async (cardId) => {
    setCardToDelete(cardId);
    setDeleteDialogOpen(true);
  };
  
  const confirmDeleteCard = async () => {
    if (!cardToDelete) return;
    console.log(cardToDelete)
    
    try {
      setIsDeleting(true);
      await walletService.deleteCard(cardToDelete);
      
      // If the deleted card was selected, reset selection
      if (selectedSavedCard && selectedSavedCard.id === cardToDelete) {
        setSelectedSavedCard(null);
      }
      
      // Refresh card list
      await fetchCards();
      addNotification("success", "Card deleted successfully");
    } catch (error) {
      console.error("Error deleting card:", error);
      addNotification("error", "Failed to delete card: " + (error.message || "Unknown error"));
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setCardToDelete(null);
    }
  };

  const handleSetDefaultCard = async (cardId) => {
    setCardToSetDefault(cardId);
    setDefaultDialogOpen(true);
  };
  
  const confirmSetDefaultCard = async () => {
    if (!cardToSetDefault) return;
    
    try {
      setIsSettingDefault(true);
      await walletService.setDefault(cardToSetDefault);
      
      await fetchCards();
      addNotification("success", "Default payment method updated successfully");
    } catch (error) {
      console.error("Error setting default card:", error);
      addNotification("error", "Failed to update default payment method: " + (error.message || "Unknown error"));
    } finally {
      setIsSettingDefault(false);
      setDefaultDialogOpen(false);
      setCardToSetDefault(null);
    }
  };

  const handleAddCard = async () => {

    // Validate card details before creating
    if (!isValidCardholderName(cardholderName)) {
      setCardholderNameValid(false);
      addNotification("error", "Please enter a valid cardholder name");
      return;
    }

    if (!isValidCardNumber(cardNumber)) {
      setCardNumberValid(false);
      addNotification("error", "Invalid card number");
      return;
    }
  
    if (!isValidCVC(cvc)) {
      setCvcValid(false);
      addNotification("error", "Invalid CVC");
      return;
    }
  
    if (!isValidExpiry(expiry)) {
      setExpiryValid(false);
      addNotification("error", "Invalid expiry date");
      return;
    }

    if (!isValidZipCode(zipCode)) {
      setZipCodeValid(false);
      addNotification("error", "Please enter a valid 5-digit zip code");
      return;
    }

    try {
      setIsCreatingCard(true);
      
      // Extract last four digits of card number
      const last_four = cardNumber.slice(-4);
      
      const cardData = {
        number: smask(cardNumber),
        expiry: smask(expiry.replace('/', '')),
        code: smask(cvc),
        title: cardholderName,
        zip: zipCode,
        last_four: last_four,
        is_default: makeDefault
      };
      
      await walletService.createCard(cardData);
      addNotification("success", "Card saved successfully");
      
      // Reset form fields
      setCardholderName("");
      setCardNumber("");
      setCvc("");
      setExpiry("");
      setZipCode("");
      setMakeDefault(false);
      
      // Refresh card list and close the form
      await fetchCards();
      setShowNewCardForm(false);
      
    } catch (error) {
      console.error("Error creating card:", error);
      addNotification("error", "Failed to save card: " + (error.message || "Unknown error"));
    } finally {
      setIsCreatingCard(false);
    }
  };

  const handleConfirmOrder = async () => {
    let formIsValid = true;

    if (paymentMethod === "card" && !selectedSavedCard && !showNewCardForm) {
      // Require selecting a saved card if available
      addNotification("error", "Please select a saved card");
      formIsValid = false;
    }

    if (!agreeToTerms) {
      setTermsError(true);
      formIsValid = false;
    }

    if (userStatus === 1) {
      addNotification("error", "Your account is disabled.");
      formIsValid = false;
      return;
    }
    
    if (!hasExistingAddress || !existingAddress || esplit[0] === "null") {
      if (!billingAddress || billingAddress.trim() === "") {
        addNotification("error", "Please provide a billing address.");
        formIsValid = false;
      }

      if (!deliveryAddress || deliveryAddress.trim() === "") {
        addNotification("error", "Please provide a delivery address.");
        formIsValid = false;
      }

      setUseExistingBillingAddress(false);
      setUseExistingDeliveryAddress(false);
    }

    // Determine shipping address
    const finalShippingAddress = useExistingDeliveryAddress
      ? existingAddress
      : deliveryAddress;

    // Determine billing address
    const finalBillingAddress = useExistingBillingAddress
      ? existingAddress
      : billingAddress;

    // Validate both addresses are not empty
    if (
      !finalShippingAddress ||
      finalShippingAddress === null ||
      finalShippingAddress.trim() === ""
    ) {
      addNotification("error", "Please provide a valid shipping address.");
      formIsValid = false;
    }

    if (
      !finalBillingAddress ||
      finalBillingAddress === null ||
      finalBillingAddress.trim() === ""
    ) {
      addNotification("error", "Please provide a valid billing address.");
      formIsValid = false;
    }

    if (!formIsValid) {
      addNotification(
        "error",
        "Please provide valid information before proceeding."
      );
      return; // Stop checkout if validation fails
    }

    try {
      const paymentTypeCode = paymentMethod === "card" ? 4 : paymentMethod === "phone" ? 8 : null;
      const cardId = paymentMethod === "card" && selectedSavedCard ? selectedSavedCard.id : null;
      
      const responseCheckout = await dispatch(
        checkout({
          billing_address: finalBillingAddress,
          shipping_address: finalShippingAddress,
          order_notes: comments,
          payment_mode: paymentTypeCode,
          card_id: cardId
        })
      );
      
      if (checkout.fulfilled.match(responseCheckout)) {
        addNotification("success", "Checkout successful");
        dispatch(clearCart());
        navigate("/my/thankyou", { state: { cartItems: cartItems } });
      }
    } catch (error) {
      addNotification("error", "Error checking out: " + error.message);
    }
  };

  const handleCheckboxChange = (e) => {
    setAgreeToTerms(e.target.checked);
    if (e.target.checked) {
      setTermsError(false);
    }
  };

  const handleCardholderNameChange = (e) => {
    const value = e.target.value;
    setCardholderName(value);
    setCardholderNameValid(isValidCardholderName(value));
  };

  const handleZipCodeChange = (e) => {
    const value = e.target.value;
    const cleaned = value.replace(/\D/g, '').slice(0, 5);
    setZipCode(cleaned);
    setZipCodeValid(isValidZipCode(cleaned));
  };

  const handleCardNumberChange = (e) => {
    const value = e.target.value;
    const cleaned = value.replace(/\D/g, '');
    setCardNumber(cleaned);
    setCardNumberValid(isValidCardNumber(cleaned));
  };
  
  const handleExpiryChange = (e) => {
    const value = e.target.value;
    // Format as MM/YY
    let cleaned = value.replace(/\D/g, '');
    
    if (cleaned.length > 4) {
      cleaned = cleaned.slice(0, 4);
    }
    
    if (cleaned.length > 2) {
      cleaned = cleaned.slice(0, 2) + '/' + cleaned.slice(2);
    }
    
    setExpiry(cleaned);
    setExpiryValid(isValidExpiry(cleaned));
  };
  
  const handleCvcChange = (e) => {
    const value = e.target.value;
    const cleaned = value.replace(/\D/g, '').slice(0, 4); 
    setCvc(cleaned);
    setCvcValid(isValidCVC(cleaned));
  };

  const handleSelectSavedCard = (card) => {
    setSelectedSavedCard(card);
    setShowNewCardForm(false);
    setPaymentMethod("card");  
  };

  const toggleNewCardForm = () => {
    if (!showNewCardForm && cardList.length >= 3) {
      addNotification("error", "Maximum of 3 cards allowed");
      return;
    }
  
    if (!showNewCardForm) {
      setCardholderName("");
      setCardNumber("");
      setCvc("");
      setExpiry("");
      setZipCode("");
      setMakeDefault(false);
    }
    
    setShowNewCardForm(!showNewCardForm);
  };

  const isValidCardNumber = (number) => {
    const cleaned = number.replace(/\D/g, '');
    return cleaned.length >= 12 && cleaned.length <= 16 && !isNaN(Number(cleaned));
  };
  
  const isValidCVC = (cvc) => {
    const cleaned = cvc.replace(/\D/g, '');
    return (cleaned.length === 3 || cleaned.length === 4) && !isNaN(Number(cleaned));
  };
  
  const isValidExpiry = (expiry) => {
    if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(expiry)) return false;
  
    const [monthStr, yearStr] = expiry.split('/');
    const inputMonth = parseInt(monthStr, 10);
    const inputYear = parseInt('20' + yearStr, 10); 
  
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();
  
    return inputYear > currentYear || (inputYear === currentYear && inputMonth >= currentMonth);
  };
  
  const isValidCardholderName = (name) => {
    return name.trim().length > 0 && /^[a-zA-Z\s'-]+$/.test(name);
  };
  
  const isValidZipCode = (zip) => {
    return zip.length === 5 && !isNaN(Number(zip));
  };
  
  useEffect(() => {
    const fetchUserInfo = async () => {
      const user = localStorage.getItem("user");
      if (user) {
        try {
          const response = await getUserInfo();
          
          const addressString = `${response.address_1}${
            response.address_2 ? `, ${response.address_2}` : ""
          }, ${response.city}, USA`;
          
          setExistingAddress(addressString);
          setUserStatus(response.status_id)
          setHasExistingAddress(true);
          setUseExistingBillingAddress(true);
          setUseExistingDeliveryAddress(true);
        } catch (error) {
          console.error("Error fetching user info:", error);
        }
      } else {
        setHasExistingAddress(false);
        setUseExistingBillingAddress(false);
        setUseExistingDeliveryAddress(false);
      }
    };
  
    fetchUserInfo();
  }, []);
  
  return (
    <Box style={{ margin: "0 auto", padding: "16px" }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ fontWeight: 600, fontSize: { xs: 20, sm: 24 }, marginTop: 3 }}
      >
        Checkout
      </Typography>
      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "24px" }}
      >
        {/* Left Column */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            maxWidth: "400px",
          }}
        >
          {/* Billing Address */}
          <Card>
            <CardHeader
              title={
                <Typography variant="h6" style={{ fontWeight: 500 }}>
                  Billing Address
                </Typography>
              }
              avatar={<PersonIcon />}
              style={{
                backgroundColor: "#C41E3A",
                color: "#FFFFFF",
                padding: "8px",
              }}
            />
            <CardContent style={{ padding: "10px" }}>
              <FormControlLabel
                value="existing"
                control={
                  <Checkbox
                    checked={useExistingBillingAddress}
                    onChange={(e) => {
                      if (esplit[0] === "null") return; // Prevent checking when no existing address
                      setUseExistingBillingAddress(e.target.checked);
                    }}
                  />
                }
                label="Use existing address"
              />
              {useExistingBillingAddress ? (
                <TextField
                  fullWidth
                  label="Existing Address"
                  margin="normal"
                  value={existingAddress}
                  disabled
                  sx={{
                    padding: "0px",
                    maxHeight: "50%",
                    marginTop: "0px",
                    marginBottom: "0px",
                  }}
                />
              ) : (
                <TextField
                  fullWidth
                  label="Billing Address"
                  margin="normal"
                  multiline
                  rows={4}
                  value={billingAddress}
                  onChange={(e) => setBillingAddress(e.target.value)}
                  placeholder="Enter billing address"
                  sx={{ marginTop: "10px" }}
                />
              )}
            </CardContent>
          </Card>

          {/* Delivery Address */}
          <Card>
            <CardHeader
              title={
                <Typography variant="h6" style={{ fontWeight: 500 }}>
                  Delivery Address
                </Typography>
              }
              avatar={<TruckIcon />}
              style={{
                backgroundColor: "#C41E3A",
                color: "#fff",
                padding: "8px",
              }}
            />
            <CardContent style={{ padding: "10px" }}>
              <FormControlLabel
                value="existing"
                control={
                  <Checkbox
                    checked={useExistingDeliveryAddress}
                    onChange={(e) => {
                      if (esplit[0] === "null") return; // Prevent checking when no existing address
                      setUseExistingDeliveryAddress(e.target.checked);
                    }}
                  />
                }
                label="Use existing address"
              />
              {useExistingDeliveryAddress ? (
                <TextField
                  fullWidth
                  label="Existing Address"
                  margin="normal"
                  value={existingAddress}
                  disabled
                  sx={{
                    padding: "0px",
                    maxHeight: "50%",
                    marginTop: "0px",
                    marginBottom: "0px",
                  }}
                />
              ) : (
                <TextField
                  fullWidth
                  label="Delivery Address"
                  margin="normal"
                  multiline
                  rows={4}
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  placeholder="Enter delivery address"
                  sx={{ marginTop: "10px" }}
                />
              )}
            </CardContent>
          </Card>

          {/* Shipping Method */}
          <Card>
            <CardHeader
              title={
                <Typography variant="h6" style={{ fontWeight: 500 }}>
                  Shipping Method
                </Typography>
              }
              avatar={<TruckIcon />}
              style={{
                backgroundColor: "#C41E3A",
                color: "#fff",
                padding: "8px",
              }}
            />
            <CardContent style={{ padding: "10px" }}>
              <RadioGroup defaultValue="flat">
                <FormControlLabel
                  value="flat"
                  control={<Radio />}
                  label="Shipping charges will be calculated after the order is processed."
                />
              </RadioGroup>
            </CardContent>
          </Card>

          {/* Payment Method */}
          <Card>
            <CardHeader
              title={
                <Typography variant="h6" style={{ fontWeight: 500 }}>
                  Payment Method
                </Typography>
              }
              avatar={<CreditCardIcon />}
              style={{
                backgroundColor: "#C41E3A",
                color: "#fff",
                padding: "8px",
              }}
            />
            <CardContent style={{ padding: "10px" }}>
              <RadioGroup
                key="payment-method-group"
                value={paymentMethod}
                onChange={(e) => {
                  setPaymentMethod(e.target.value);
                  // Reset selections when switching payment methods
                  if (e.target.value !== "card") {
                    setSelectedSavedCard(null);
                    setShowNewCardForm(false);
                  }
                }}
              >
                <FormControlLabel
                  value="phone"
                  control={<Radio />}
                  label="Pay by Phone"
                />
                <FormControlLabel
                  value="card"
                  control={<Radio />}
                  label="Pay by Credit Card"
                />
                
                {paymentMethod === "card" && (
                  <Box sx={{ mt: 2 }}>
                    {isLoadingCards ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : (
                      <>
                        {cardList.length > 0 && (
                          <>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight:"bold" }}>Saved Cards</Typography>
                            {cardList.map((card, index) => (
                              <SavedCardItem 
                                key={card.id || index}
                                card={card}
                                isSelected={selectedSavedCard && selectedSavedCard.id === card.id}
                                onSelect={handleSelectSavedCard}
                                setPaymentMethod={setPaymentMethod} 
                                onDelete={handleDeleteCard}
                                onSetDefault={handleSetDefaultCard}

                              />
                            ))}
                          </>
                        )}
                        
                        {!showNewCardForm ? (
                          cardList.length < 3 ? (
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<AddIcon />}
                              onClick={toggleNewCardForm}
                              size="small"
                              sx={{ mt: 2 }}
                            >
                              Add Card
                            </Button>
                          ) : (
                            <Typography color="text.secondary" sx={{ mt: 2, fontSize: '0.875rem' }}>
                              *Maximum of 3 cards allowed
                            </Typography>
                          )
                        ) : (
                          <>
                            {/* <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Add New Card</Typography> */}
                            <NewCardForm
                              cardholderName={cardholderName}
                              setCardholderName={setCardholderName}
                              cardholderNameValid={cardholderNameValid}
                              cardNumber={cardNumber}
                              setCardNumber={setCardNumber}
                              cardNumberValid={cardNumberValid}
                              setCvc={setCvc}
                              cvcValid={cvcValid}
                              expiry={expiry}
                              setExpiry={setExpiry}
                              expiryValid={expiryValid}
                              zipCode={zipCode}
                              setZipCode={setZipCode}
                              zipCodeValid={zipCodeValid}
                              saveCard={makeDefault}
                              setSaveCard={setMakeDefault}
                              makeDefault={makeDefault}
                              setMakeDefault={setMakeDefault}
                              handleCardholderNameChange={handleCardholderNameChange}
                              handleCardNumberChange={handleCardNumberChange}
                              handleCvcChange={handleCvcChange}
                              handleExpiryChange={handleExpiryChange}
                              handleZipCodeChange={handleZipCodeChange}
                              onAddCard={handleAddCard}
                              isCreatingCard={isCreatingCard}
                            />
                            <Button
                              variant="text"
                              color="secondary"
                              onClick={toggleNewCardForm}
                              sx={{ mt: 1 }}
                            >
                              Cancel
                            </Button>
                          </>
                        )}

                      </>
                    )}
                  </Box>
                )}
              </RadioGroup>
            </CardContent>
          </Card>
          </div>

         <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
           {/* Payment Details */}
           <Card>
             <CardHeader
              title={
                <Typography variant="h6" style={{ fontWeight: 500 }}>
                  Shopping Cart
                </Typography>
              }
              avatar={<ShoppingCartIcon />}
              style={{
                backgroundColor: "#C41E3A",
                color: "#fff",
                padding: "8px",
              }}
            />
            <Box sx={{ padding: "24px" }}>
              <CheckoutCart cartItems={cartItems} />
            </Box>
            <Card style={{ padding: "24px" }}>
              <CardHeader
                title={
                  <Typography variant="h6" style={{ fontWeight: 500 }}>
                    Payment Details
                  </Typography>
                }
                avatar={<PhoneIcon />}
                style={{
                  backgroundColor: "#C41E3A",
                  color: "#fff",
                  padding: "8px",
                  borderRadius: "4px",
                }}
              />
              <CardContent>
                {paymentMethod === "phone" && (
                  <>
                    <Typography
                      style={{ fontWeight: 550, fontSize: { xs: 20, sm: 24 } }}
                    >
                      Pay by Phone Instructions
                    </Typography>
                    <Typography variant="body1">
                      CALL OR TEXT FOR PAYMENT WITH YOUR ORDER NUMBER AT
                      704-240-2025 ***Vape and/or Nicotine Tax will be added to
                      your final invoice according to your State Law***Credit
                      Card Fee of 2% will be added if paying by credit card***
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Card>

          {/* Comments */}
          <Card>
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  style={{ fontWeight: 500, paddingLeft: "6px" }}
                >
                  Add Comments About Your Order
                </Typography>
              }
              style={{
                backgroundColor: "#C41E3A",
                color: "#fff",
                padding: "8px",
              }}
            />
            <CardContent>
              <TextField
                multiline
                fullWidth
                rows={4}
                label="Comments"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreeToTerms}
                    onChange={handleCheckboxChange}
                  />
                }
                label="I agree to the Terms & Conditions"
              />
              {termsError && (
                <FormHelperText
                  error
                  style={{
                    fontSize: "12px",
                    marginTop: "0px",
                    marginBottom: "2px",
                  }}
                >
                  **You must agree to the Terms & Conditions to proceed.
                </FormHelperText>
              )}
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleConfirmOrder}
              >
                Confirm Order
              </Button>
            </CardContent>
          </Card>
          {/* Confirmation Dialog */}
          <ConfirmationDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          title="Delete Card"
          message="Are you sure you want to delete this card? This action cannot be undone."
          confirmText="Delete"
          confirmColor="error"
          onConfirm={confirmDeleteCard}
          isProcessing={isDeleting}
        />

        {/* Set Default Card Dialog */}
        <ConfirmationDialog
          open={defaultDialogOpen}
          onClose={() => setDefaultDialogOpen(false)}
          title="Set Default Payment Method"
          message="Would you like to set this card as your default payment method?"
          confirmText="Set as Default"
          onConfirm={confirmSetDefaultCard}
          isProcessing={isSettingDefault}
        />
        </div>
      </div>
    </Box>
  );
}

